import { T, useTranslate } from '@tolgee/react';
import { Progress } from 'antd';
import { useRouter } from 'next/router';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Countdown from 'react-countdown';
import styled from 'styled-components';

import theme from '../../assets/theme';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import useGame from '../../hooks/useGame';
import { useGameParticipants } from '../../hooks/useGameParticipants';
import useGameSaveSocket from '../../hooks/useGameSaveSocket';
import useLobby from '../../hooks/useLobby';
import { HostPresentationMediaPrefetcher } from '../../hooks/useMediaPreFetcher';
import { useMetrics } from '../../hooks/useMetrics';
import useShowMaintanancePopup from '../../hooks/useShowMaintanancePopup';
import { useHideIntercom } from '../../providers/CustomIntercomStateProvider';
import LiveActionsDisabledProvider from '../../providers/LiveActionsDisabledProvider';
import { TimesyncContext } from '../../providers/TimesyncProvider/TimesyncProvider';
import { Game } from '../../services/backendService/types';
import {
  getHasNextActivityOrSlide,
  getHasPreviousActivityOrSlide,
  getRoundCategory,
  RoundState,
} from '../../utils/roundUtils';
import { zIndexes } from '../../utils/zIndexes';
import SimpleLiveGameWidget from '../_molecules/SimpleLiveGameWidget';
import { PinCodeNudger } from '../SlideComponents/PinCodeNudger';
import { findRelatedSlidesWithoutIndex } from '../SlideComponents/slideUtil';
import SlideWrapperViwer from '../SlideComponents/SlideWrapperViewer';
import { CenteredContainer } from '../styled/CenteredContainer';
import SimpleLiveGameActionButtons from './SimpleLiveGameActionButtons';

const LiveGame = () => {
  useShowMaintanancePopup();
  const { now } = useContext(TimesyncContext);
  const router = useRouter();
  const metrics = useMetrics();
  useHideIntercom();
  const { gameId, tenantId } = router.query as { gameId: string; tenantId: string };
  const { participants, count: participantsCount } = useGameParticipants(
    gameId,
    tenantId,
  );
  const { t } = useTranslate();
  const { autoSave } = useGameSaveSocket();
  const [moderationTooltipHasBeenShown, setModerationTooltipHasBeenShown] = useState(
    false,
  );

  const { game, isFetching, roundState, setRoundState } = useGame(gameId);

  const isFirstRoundOfActivity = getIsFirstSlideOfActivity(game);
  const [showActivityMedia, setShowActivityMedia] = useState(false);
  const isActivityWithSkip = getIsActivityWithSkip(roundState, game);
  const [isEndGameModalVisible, setIsEndGameModalVisible] = useState(false);
  const { setShowLobby } = useLobby(game?.id);
  const canStartActivity = isFirstRoundOfActivity && roundState === 'pre';
  const currentSlide = useMemo(
    () => game?.slides?.find(r => r.id === game?.currentRound),
    [game],
  );

  const responseSlideNotWasPlayed =
    !currentSlide?.wasPlayed &&
    (currentSlide?.type === 'podium' || currentSlide?.type === 'responsegrid');
  const showResultView = useMemo(() => {
    return roundState === 'post' && showActivityMedia === false;
  }, [roundState, showActivityMedia]);
  const onCountDownCompleted = useCallback(() => {
    if (game?.roundFreezedAt) return;
    if (game?.slides) {
      if (game && game.slides) {
        const currentSlideIndex = game.currentRound
          ? game.slides.findIndex(round => round.id === game.currentRound)
          : -1;
        const currentSlide = game.slides[currentSlideIndex];
        const newSlides = game.slides.map(r =>
          r.id === currentSlide.id ? { ...currentSlide, wasPlayed: true } : r,
        );
        const nextSlide = game.slides[currentSlideIndex + 1];
        const shouldAutoProgressToNextRound =
          currentSlide.type === 'voting' &&
          getRoundCategory(nextSlide.type) === 'results';

        autoSave({
          game: {
            ...game,
            slides: newSlides,
            currentRound: shouldAutoProgressToNextRound ? nextSlide.id : currentSlide.id,
            modifiedAt: now(),
          },
        });
      }
    }
  }, [game, autoSave, now]);
  const handleTopbarPinClicked = useCallback(() => {
    metrics.logEvent('LiveGame.Pin.Click');
    metrics.logEvent('LiveGame.Lobby.Open', { byPinClick: true });
    setShowLobby(true);
  }, [metrics, setShowLobby]);
  const handleSetSlideWasPlayed = useCallback(
    (slideId: string) => {
      if (game?.slides) {
        const slide = game.slides.find(r => r.id === slideId);

        if (!slide) return;
        if (!slide.wasPlayed) {
          autoSave({
            game: {
              ...game,
              slides: game.slides.map(r =>
                r.id === slideId ? { ...r, wasPlayed: true } : r,
              ),
              modifiedAt: now(),
            },
          });
        }
      }
    },
    [autoSave, game, now],
  );

  const slideWasPlayed = useCallback(() => {
    currentSlide && handleSetSlideWasPlayed(currentSlide.id);
  }, [currentSlide, handleSetSlideWasPlayed]);
  const backgroundMedia = useMemo(() => {
    if (!currentSlide) return undefined;
    if (!game?.slides) return undefined;
    const relatedSlides = findRelatedSlidesWithoutIndex(currentSlide.id, game.slides);
    return relatedSlides[0]?.backgroundMedia;
  }, [currentSlide, game?.slides]);

  return (
    <LiveActionsDisabledProvider>
      {game?.slides && (
        <HostPresentationMediaPrefetcher tenantId={tenantId} slides={game?.slides} />
      )}
      {game && game.slides && (
        <GameContainer>
          {currentSlide && (
            <>
              <SlideWrapperViwer
                aspect={{ height: 1080, width: 1920 }}
                slide={currentSlide}
                refetchData={true}
                backgroundMedia={backgroundMedia}
                resultView={showResultView}
                showStaticPoll={roundState !== 'post'}
                hideLogo={false}
                gameId={gameId}
                tenantId={tenantId}
                onSlideWasPlayedTimeout={slideWasPlayed}
                slides={game.slides}
                isDiscoverTemplate={false}
                lessonAttachments={game.attachments}
              >
                <>
                  <PinCodeNudger
                    gameId={gameId}
                    onPinClick={handleTopbarPinClicked}
                    backgroundBrightness={currentSlide.backgroundMedia?.brightness}
                  />

                  {game.roundStartsAt && roundState === 'countdown' && (
                    <ActivityStartingCountdown
                      date={new Date(game.roundStartsAt)}
                      setRoundState={setRoundState}
                    />
                  )}
                </>
              </SlideWrapperViwer>
              <SimpleLiveGameActionButtons
                game={game}
                slides={game.slides}
                currentSlide={currentSlide}
                isNavigationDisabled={!isActivityWithSkip || responseSlideNotWasPlayed}
                setShowMedia={setShowActivityMedia}
                tenantId={tenantId}
                roundState={roundState}
                setRoundState={setRoundState}
                participants={participants}
                participantsCount={participantsCount}
                showActivityMedia={showActivityMedia}
                isEndGameModalVisible={isEndGameModalVisible}
                setGameModalVisible={setIsEndGameModalVisible}
              />
              {!game.isShowingLobby && (
                <SimpleLiveGameWidget
                  game={game}
                  roundState={roundState}
                  tenantId={tenantId}
                  moderationTooltipHasBeenShown={moderationTooltipHasBeenShown}
                  setModerationTooltipHasBeenShown={setModerationTooltipHasBeenShown}
                  canStartActivity={canStartActivity}
                  setShowMedia={setShowActivityMedia}
                  onCountDownCompleted={onCountDownCompleted}
                  currentSlide={currentSlide}
                  participantCount={participantsCount}
                  setGameModalVisible={setIsEndGameModalVisible}
                />
              )}
            </>
          )}
        </GameContainer>
      )}
      {!game && !isFetching && (
        <CenteredContainer style={{ paddingTop: 20, height: '100%' }}>
          <h1>
            {t(
              'UpsGameDoesNotExists',
              'Oops this game does not exist, please go back and recreate from the lesson',
            )}
          </h1>
        </CenteredContainer>
      )}
    </LiveActionsDisabledProvider>
  );
};

export const calculateRemainingTime = (now: () => number, end: number): number => {
  const flooredSeconds = Math.floor((end - now()) / 1000);
  return flooredSeconds;
};

export const toActivity = (type: string) => {
  if (type === 'answers')
    return <T keyName="Written Answers" defaultValue="Written Answers" />;
  if (type === 'aiAssessedAnswers')
    return <T keyName="AI Feedback" defaultValue="AI Feedback" />;
  if (type === 'aiAssessedImageAnswers')
    return <T keyName="AI Whiteboard" defaultValue="AI Whiteboard" />;
  if (type === 'canvas') return <T keyName="Drawings" defaultValue="Drawings" />;
  if (type === 'wordcloud') return <T keyName="Wordcloud" defaultValue="Wordcloud" />;
  if (type === 'ideas') return <T keyName="Ideas" defaultValue="Ideas" />;
  if (type === 'poll') return <T keyName="Poll" defaultValue="Poll" />;
  if (type === 'voting') return <T keyName="Votes" defaultValue="Votes" />;
  if (type === 'text_image') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'image_text') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'image') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'fullscreen_media') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'header') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'bullets') return <T keyName="Slide" defaultValue="Slide" />;
  if (type === 'slide') return <T keyName="Slide" defaultValue="Slide" />;
};

export const getHasNextActivity = (game?: Game) => {
  if (!game) return false;
  if (!game.currentRound) return false;
  if (!game.slides) return false;
  return getHasNextActivityOrSlide(game.slides, game.currentRound);
};

export const getHasPreviousActivity = (game?: Game) => {
  if (!game) return false;
  if (!game.currentRound) return false;
  if (!game.slides) return false;
  return getHasPreviousActivityOrSlide(game.slides, game.currentRound);
};

const getIsFirstSlideOfActivity = (game?: Game) => {
  if (!game) return false;
  if (!game.currentRound) return false;
  if (!game.slides) return false;

  const relatedSlides = findRelatedSlidesWithoutIndex(game.currentRound, game.slides);
  const firstSlide = relatedSlides[0];
  if (!firstSlide) return false;
  return firstSlide.id === game.currentRound && firstSlide.type !== 'slide';
};

export const getIsLastSlideOfActivity = (game?: Game) => {
  if (!game) return false;
  if (!game.currentRound) return false;
  if (!game.slides) return false;

  const relatedSlides = findRelatedSlidesWithoutIndex(game.currentRound, game.slides);
  const lastSlides = relatedSlides[relatedSlides.length - 1];
  return lastSlides.id === game.currentRound && lastSlides.type !== 'slide';
};

export const getRoundState = ({
  game,
  now,
}: {
  game?: Game;
  now: () => number;
}): RoundState | undefined => {
  //TODO: Add unit tests to this util function now that we have added post-between
  if (!game) return undefined;

  const currentSlide = game.slides?.find(r => r.id === game?.currentRound);

  const isNotTheLastSlide =
    currentSlide?.type !== 'podium' &&
    currentSlide?.type !== 'responsegrid' &&
    currentSlide?.type !== 'poll' &&
    currentSlide?.type !== 'wordcloud' &&
    currentSlide?.type !== 'slide';

  const wasPlayed = currentSlide?.wasPlayed;
  if (wasPlayed) {
    return isNotTheLastSlide ? 'post-between' : 'post';
  }
  // The current round has not yet started
  const isPreRoundState = game && game.roundStartsAt === null;
  if (isPreRoundState) return 'pre';

  // The current round is starting in a few seconds
  const isRoundCountdownState =
    game && game.roundStartsAt !== null && game.roundStartsAt >= now();

  if (isRoundCountdownState) return 'countdown';

  // The current round is done soon
  const isActiveRoundState =
    game &&
    game.roundStartsAt !== null &&
    game.roundStartsAt <= now() &&
    currentSlide &&
    currentSlide.durationSeconds &&
    game.roundStartsAt + currentSlide.durationSeconds * 1000 >= now();

  if (isActiveRoundState) return 'in';

  // The current round was done a few seconds ago
  const isPostRoundState =
    game &&
    game.roundStartsAt !== null &&
    currentSlide &&
    currentSlide.durationSeconds &&
    game.roundStartsAt + currentSlide.durationSeconds * 1000 <= now();

  if (isPostRoundState) return isNotTheLastSlide ? 'post-between' : 'post';
  return isNotTheLastSlide ? 'post-between' : 'post';
};
export const getIsActivityWithSkip = (roundState?: string, game?: Game) => {
  if (!game) return true;
  if (!game.slides) return true;
  if (!roundState) return true;

  if (roundState === 'pre') return true;
  if (roundState === 'post') return true;

  return false;
};

const draw = (start: number, end: number, now: () => number) => {
  const res = ((now() - start) / (end - start)) * 100;
  return res;
};

const ActivityStartingCountdown = ({
  date,
  setRoundState,
}: {
  date: Date;
  setRoundState: Dispatch<SetStateAction<RoundState | undefined>>;
}) => {
  const { now } = useContext(TimesyncContext);
  const [start] = useState(now());
  const isDektop = useBreakPoint(900);
  const { t } = useTranslate();
  const [firstStop] = useState(date.getTime() - 200 - (date.getTime() - start) / 2);
  const [showReadyPercent, setShowReadyPercent] = useState(true);
  const [percent, setPercent] = useState(0);
  const [goPercent, setGoPercent] = useState(0);
  const [showGoPercent, setShowGoPercent] = useState(false);

  useEffect(() => {
    if (percent >= 95) {
      setShowReadyPercent(false);
      setShowGoPercent(true);
    }
  }, [percent]);

  useEffect(() => {
    let ref: NodeJS.Timeout;
    if (showGoPercent) {
      const msTicks = 1000 / 60;
      ref = setInterval(() => {
        setGoPercent(draw(firstStop, date.getTime() - 400, now));
      }, msTicks);
    }

    return () => {
      if (ref) clearInterval(ref);
    };
  }, [date, showGoPercent, firstStop, now]);

  useEffect(() => {
    const msTicks = 1000 / 60;
    const ref = setInterval(() => {
      setPercent(draw(start, firstStop, now));
    }, msTicks);

    return () => {
      clearInterval(ref);
    };
  }, [date, start, firstStop, now]);

  const handleCountDownCompleted = useCallback(() => {
    setRoundState('in');
  }, [setRoundState]);

  return (
    <CenteredContainer
      style={{
        flexDirection: 'column',
        flex: '1 1 auto',
        position: 'absolute',
        top: '0',
        left: '0',
        height: '100%',
        width: '100%',
        zIndex: zIndexes('LIVEGAME_COUNTDOWN_OVERLAY'),
      }}
    >
      {showReadyPercent && (
        <ProgressBackground $showShadow $width={isDektop ? 400 : 300}>
          <Progress
            strokeColor={theme.colors.orange}
            trailColor={theme.colors.white}
            width={isDektop ? 400 : 300}
            type="circle"
            percent={percent}
            format={() => (
              <span style={isDektop ? { fontSize: '48px' } : { fontSize: '30px' }}>
                {t('Ready')}
              </span>
            )}
          />
        </ProgressBackground>
      )}
      {showGoPercent && (
        <ProgressBackground $showShadow $width={isDektop ? 400 : 300}>
          <Progress
            strokeColor={theme.colors.orange}
            trailColor={theme.colors.white}
            width={isDektop ? 400 : 300}
            type="circle"
            percent={goPercent}
            format={() => (
              <span style={isDektop ? { fontSize: '48px' } : { fontSize: '30px' }}>
                {t('Set')}{' '}
              </span>
            )}
          />
        </ProgressBackground>
      )}
      <Countdown
        now={now}
        date={date}
        renderer={() => null}
        onComplete={handleCountDownCompleted}
      />
    </CenteredContainer>
  );
};
export const SlideWrapperOverlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const GameContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  /* background-color: ${theme.colors.background}; */
  background-color: black;
  z-index: 0;
`;

const ProgressBackground = styled.div<{ $width: number; $showShadow?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 500px;
  margin-top: 10px;
  height: ${({ $width }) => $width}px;
  width: ${({ $width }) => $width}px;
  background-color: ${theme.colors.white};
  box-shadow: ${({ $showShadow }) =>
    $showShadow ? '0px 2px 20px rgba(0, 0, 0, 0.2)' : 'none'};

  .ant-progress-text {
    font-size: 22px;
    font-weight: bold;
    color: ${theme.colors.black} !important;
  }
  .ant-progress-status-success .ant-progress-text {
    color: ${theme.colors.black};
  }

  @media (max-width: 990px) {
    .ant-progress-text {
      font-size: 14px;
    }
  }
  @media (max-width: 500px) {
    .ant-progress-text {
      font-size: 10px;
    }
  }
`;

const RootLiveUpdaterText = styled.p`
  color: ${theme.colors.black};
  font-weight: bold;
  text-align: center;
  margin-bottom: 0;
`;

export const LiveUpdaterCounter = styled(RootLiveUpdaterText)`
  font-size: 65px;
  line-height: 65px;

  @media (max-width: 1050px) {
    font-size: 55px;
    line-height: 55px;
  }
  @media (max-width: 550px) {
    font-size: 30px;
    line-height: 30px;
  }
`;
export const LiveUpdaterType = styled(RootLiveUpdaterText)`
  font-size: 50px;
  line-height: 50px;

  @media (max-width: 1050px) {
    font-size: 35px;
    line-height: 35px;
  }
  @media (max-width: 550px) {
    font-size: 20px;
    line-height: 20px;
  }
`;

export default LiveGame;
